import * as Y from "yjs";
export function widgetsMap(doc) {
    return doc.getMap("widgets");
}
export function metaMap(doc) {
    return doc.getMap("meta");
}
/**
 * The VSM graph node container: `nodeId → Y.Map<{ kind, x, y, w, h, label, … }>`
 * (symbols and lanes share the map via a `kind` discriminator, ADR-0021). Lazily
 * created on first access, like `widgetsMap`; entries are written by the VSM
 * module (PB-197) — this accessor only yields the container.
 */
export function nodesMap(doc) {
    return doc.getMap("nodes");
}
/**
 * The VSM graph arrow container. The persisted top-level key remains `edges`
 * because changing a Yjs container name would rewrite live documents; the public
 * accessor uses the approved arrow vocabulary. Entries are keyed by their own
 * identity and stored as one nested map.
 */
export function arrowsMap(doc) {
    return doc.getMap("edges");
}
/**
 * VSM arrow deletion metadata. Keep this accessor beside the other document
 * containers so feature code never reaches for `doc.getMap` directly.
 */
export function arrowDeleteReceiptsMap(doc) {
    return doc.getMap("arrowDeleteReceipts");
}
/** Future-only idea notes. They are annotations, never REST-backed VSM shapes. */
export function vsmFutureIdeaNotesMap(doc) {
    return doc.getMap("vsmFutureIdeaNotes");
}
/** Slot allocation metadata for the isolated Future-note container. */
export function vsmFutureIdeaNoteMetaMap(doc) {
    return doc.getMap("vsmFutureIdeaNoteMeta");
}
/**
 * Legacy VSM workshop-progression data retained for rollback and repair. Live
 * room phase is relational-primary; new clients do not write these keys.
 */
export function workshopMap(doc) {
    return doc.getMap("workshop");
}
/** @deprecated ORPHANED — legacy rollback/data-repair material only. */
export function readVsmLastPhaseTransition(workshop) {
    const value = workshop.get("lastPhaseTransition");
    if (!value || typeof value !== "object" || Array.isArray(value))
        return null;
    const candidate = value;
    return candidate.schemaVersion === 1 &&
        typeof candidate.phaseId === "string" &&
        typeof candidate.actorUserId === "string" &&
        typeof candidate.at === "number"
        ? {
            schemaVersion: 1,
            phaseId: candidate.phaseId,
            actorUserId: candidate.actorUserId,
            at: candidate.at,
        }
        : null;
}
/** @deprecated ORPHANED — legacy rollback/data-repair material only. */
export function writeVsmLastPhaseTransition(workshop, transition) {
    const write = () => {
        workshop.set("lastPhaseTransition", transition);
    };
    if (workshop.doc)
        workshop.doc.transact(write);
    else
        write();
}
/** Current Y.Doc schema version. Bump only via an explicit migration. */
export const CANVAS_SCHEMA_VERSION = 4;
/**
 * Ensure the doc has the canonical top-level structure. Called by the canvas
 * page once on first render so callers can safely assume `meta.schemaVersion`
 * is set even on a brand-new doc. Existing numeric stamps are preserved, so v3
 * documents remain v3 and v4 readers must accept them. The maps are NOT
 * force-seeded here — they are lazily materialized by their accessors on first
 * access (an empty Y.Map contributes nothing to the doc state), so this version
 * bump adds no shape/arrow structure to SOLVE/Hoshin docs (which never touch those
 * accessors).
 */
export function ensureCanvasShape(doc) {
    const meta = metaMap(doc);
    if (typeof meta.get("schemaVersion") !== "number") {
        doc.transact(() => {
            meta.set("schemaVersion", CANVAS_SCHEMA_VERSION);
            if (!(meta.get("title") instanceof Y.Text)) {
                meta.set("title", new Y.Text());
            }
        });
    }
}
/** Read the current title as a plain string. */
export function readTitle(doc) {
    const t = metaMap(doc).get("title");
    return t instanceof Y.Text ? t.toString() : typeof t === "string" ? t : "";
}
//# sourceMappingURL=doc-shape.js.map